<template>
  <details>
    <summary>{{ title }}</summary>
    <label class="range-label">Time range
      <select v-model.number="days" @change="load(false)">
        <option :value="3">Past 72 hours</option><option :value="7">Past 1 week</option>
        <option :value="14">Past 2 weeks</option><option :value="30">Past 1 month (30 days)</option>
      </select>
    </label>
    <p>Times shown in Central Time.</p>
    <button type="button" :disabled="loading" @click="load(false)">Refresh history</button>
    <p v-if="error" role="alert">{{ error }}</p>
    <p v-if="loading" role="status">Loading history…</p>
    <p v-else-if="!visibleRows.length && !error">{{ cursor ? 'No matching events on this page. Load older events to continue.' : 'No events in this time range.' }}</p>
    <ul>
      <li v-for="event in visibleRows" :key="event.id">
        <strong>{{ label(event) }}</strong><br />{{ date(event.time) }} · {{ event.actor }}
        <p v-if="event.reason">{{ event.reason }}</p>
        <p v-if="event.signIn">
          {{ event.signIn.authenticationMethod }}<br />
          IP: {{ event.signIn.ipAddress }} · Approximate location: {{ event.signIn.location }}<br />
          Reported device: {{ event.signIn.device }} · Reported browser: {{ event.signIn.browser }}<br />
          Network type: {{ event.signIn.networkType }}
        </p>
      </li>
    </ul>
    <button v-if="cursor" type="button" :disabled="loading" @click="load(true)">Load older events</button>
  </details>
</template>
<script>
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { httpsCallable } from 'firebase/functions';
import { projectFunctions } from '@/firebase/firebase';
const listHistory = httpsCallable(projectFunctions, 'listAdminAuthHistory');
export default {
  props: { uid: String, category: String, title: String, revision: Number },
  setup(props) {
    const days = ref(3), rows = ref([]), cursor = ref(null), loading = ref(false), error = ref('');
    const eventTypes = { password: ['password_reset_requested', 'password_reset_email', 'password_change_observed'], signIn: ['admin_sign_in'], recovery: ['mfa_recovery'] };
    const windowEnd = ref(Date.now());
    const visibleRows = computed(() => rows.value.filter(event =>
      eventTypes[props.category]?.includes(event.type) && event.time >= windowEnd.value - days.value * 86400000 && event.time <= windowEnd.value));
    let generation = 0;
    const date = value => new Intl.DateTimeFormat('en-US', {dateStyle:'medium',timeStyle:'short',timeZone:'America/Chicago'}).format(new Date(value));
    const label = event => event.type === 'admin_sign_in' ? 'Successful admin session start' : event.type === 'mfa_recovery' ? `Authenticator recovery: ${event.status}` : event.type === 'password_change_observed' ? 'Password change confirmed by Firebase' : event.type === 'password_reset_requested' ? 'Password reset requested' : event.status === 'accepted' ? 'Reset email accepted by provider' : 'Reset email failed or unconfirmed';
    const load = async (append = false) => {
      const current = ++generation;
      loading.value = true; error.value = '';
      if (!append) { windowEnd.value = Date.now(); rows.value = []; cursor.value = null; }
      try {
        const {data} = await listHistory({uid:props.uid,category:props.category,days:days.value,cursor:append ? cursor.value : null});
        if (current !== generation) return;
        rows.value = append ? [...rows.value, ...data.events] : data.events;
        if (data.nextCursor?.until) windowEnd.value = data.nextCursor.until;
        cursor.value = data.nextCursor;
      } catch { if (current === generation) error.value = 'Unable to load history. Please retry.'; }
      finally { if (current === generation) loading.value = false; }
    };
    watch(() => [props.uid, props.category, props.revision], () => void load(), {immediate:true});
    onBeforeUnmount(() => { generation++; });
    return {days,visibleRows,cursor,loading,error,date,label,load};
  }
};
</script>
<style scoped>
summary { cursor: pointer; font-weight: 700; padding: 10px 0; }
.range-label { display: flex; align-items: center; gap: 12px; margin: 12px 0; }
select { padding: 8px; border: 1px solid #dce4df; border-radius: 8px; background: white; }
p, li { font-size: .9rem; overflow-wrap: anywhere; }
li { margin-bottom: 12px; }
button { border: 0; border-radius: 8px; background: #18ab69; color: white; padding: 10px 16px; margin-bottom: 12px; }
button:disabled { opacity: .5; }
[role="alert"] { color: #a33636; }
</style>
